import { findVerse } from '@rb/shared/admin-verses';
import { useCallback, useEffect, useState } from 'react';
import { readAuthToken } from '@/auth/auth-token';
import { cdnChannel, fetchCdnCollection } from '@/client/cdn-sheets';
import { createShopItem, listShopItems, updateShopItem } from '@/client/vxshop.client';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useVerseOwnership } from '@/shop/use-verse-ownership';
import { useEffectiveGatewayId } from '@/verse/active-verse.store';
import { buildUpsertPlan, type ClientProduct, type UpsertPlan } from '@/shop/vxshop-mapping';

const ENVS = ['dev', 'staging', 'prod'] as const;

interface Props {
  onClose: () => void;
}

/**
 * Master Data Editor 의 ActionBar 에서 열리는 Shop Upsert 팝업.
 * 열리면 자동으로 변경사항(plan)을 미리보기하고, 검토 후 적용한다.
 * 기준 데이터는 export 된 CDN clientProducts JSON 이다(상단 경고 참고).
 */
export function ShopUpsertPanel({ onClose }: Props) {
  const { account, activeVerseId, registeredEnv, owned } = useVerseOwnership();
  const gatewayId = useEffectiveGatewayId();
  const [env, setEnv] = useState<string>(registeredEnv ?? 'dev');
  const [testMode, setTestMode] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState<UpsertPlan | null>(null);
  const [error, setError] = useState('');
  const [applyResult, setApplyResult] = useState<{
    created: number;
    updated: number;
    deactivated: number;
    failed: number;
    failures: string[];
  } | null>(null);

  const envLocked = registeredEnv !== null;
  const effectiveEnv = envLocked ? registeredEnv : env;

  // verse 가 바뀌면 env 픽커를 새 verse 기준으로 재동기화(잠긴 경우 effectiveEnv 가 registeredEnv 를 쓰므로 무해).
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeVerseId is an intentional re-sync trigger
  useEffect(() => {
    setEnv(registeredEnv ?? 'dev');
  }, [registeredEnv, activeVerseId]);

  const buildPlan = useCallback(async () => {
    if (!activeVerseId || !owned) return;
    setPlanning(true);
    setError('');
    setPlan(null);
    setApplyResult(null);
    try {
      const token = readAuthToken();
      if (!token) throw new Error('로그인 토큰이 없습니다. 다시 로그인하세요.');
      const products = await fetchCdnCollection<ClientProduct[]>(effectiveEnv, 'clientProducts');
      const serverItems = await listShopItems({ token, verseId: activeVerseId, gatewayId });
      // 등록 verse 면 flavor 로 노출 분리, 미등록(custom) verse 면 undefined → 필터 없음(전체 push).
      const targetFlavor = findVerse(activeVerseId)?.flavor;
      setPlan(buildUpsertPlan(products, serverItems, activeVerseId, testMode, targetFlavor));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPlanning(false);
    }
  }, [activeVerseId, owned, effectiveEnv, testMode, gatewayId]);

  // 팝업이 열리거나 verse/env/testMode 가 바뀌면 자동으로 미리보기 갱신.
  useEffect(() => {
    void buildPlan();
  }, [buildPlan]);

  const handleApply = async () => {
    if (!activeVerseId || !plan) return;
    if (
      registeredEnv === 'prod' &&
      !window.confirm('prod verse 의 상점에 적용합니다. 계속하시겠습니까?')
    )
      return;
    const token = readAuthToken();
    if (!token) {
      setError('로그인 토큰이 없습니다.');
      return;
    }
    setApplying(true);
    setError('');
    const auth = { token, verseId: activeVerseId, gatewayId };
    let created = 0;
    let updated = 0;
    let deactivated = 0;
    let failed = 0;
    const failures: string[] = [];
    try {
      for (const c of plan.creates) {
        try {
          await createShopItem(auth, c.payload);
          created++;
        } catch (e) {
          failed++;
          failures.push(e instanceof Error ? e.message : String(e));
        }
      }
      for (const u of plan.updates) {
        try {
          await updateShopItem(auth, u.serverItemId, u.payload);
          updated++;
        } catch (e) {
          failed++;
          failures.push(e instanceof Error ? e.message : String(e));
        }
      }
      for (const d of plan.deactivates) {
        try {
          await updateShopItem(auth, d.serverItemId, d.payload);
          deactivated++;
        } catch (e) {
          failed++;
          failures.push(e instanceof Error ? e.message : String(e));
        }
      }
      setApplyResult({ created, updated, deactivated, failed, failures });
    } finally {
      setApplying(false);
    }
  };

  const changeCount = plan
    ? plan.creates.length + plan.updates.length + plan.deactivates.length
    : 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Shop Upsert</DialogTitle>
          <DialogDescription className="sr-only">
            export 된 CDN clientProducts JSON 을 기준으로 선택된 verse 의 VX Shop 을
            생성/갱신합니다.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <AlertTitle>주의 — export 된 JSON 기준으로 동작합니다</AlertTitle>
          <AlertDescription>
            이 작업은 <strong>export 된 CDN clientProducts JSON</strong> (
            <span className="font-mono">rb-sheets/{cdnChannel(effectiveEnv)}/clientProducts</span>)
            을 기준으로 선택된 verse 의 VX Shop 에 생성/갱신합니다. 최신 마스터데이터를 반영하려면
            먼저 <strong>Apply</strong> 로 R2 에 export 했는지 확인하세요. (stock 수량 변경은 update
            대상으로 감지하지 않습니다.)
          </AlertDescription>
        </Alert>

        {!owned && (
          <Alert variant="destructive">
            <AlertTitle>실행 불가</AlertTitle>
            <AlertDescription>
              이 verse 의 소유 계정으로 로그인해야 합니다.
              <br />
              verse: <span className="font-mono">{activeVerseId ?? '(선택 안 됨)'}</span>
              <br />
              로그인 계정: <span className="font-mono">{account ?? '-'}</span>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-3 rounded-md border border-border bg-card px-3 py-3">
          <div className="flex flex-col gap-2">
            <Label htmlFor="shop-env-select" className="text-xs text-muted-foreground">
              Source env {envLocked && '(등록 verse — 자동)'}
            </Label>
            <Select value={effectiveEnv} onValueChange={setEnv} disabled={envLocked}>
              <SelectTrigger id="shop-env-select" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENVS.map((e) => (
                  <SelectItem key={e} value={e}>
                    {e}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label htmlFor="test-mode-checkbox" className="flex items-center gap-2 text-sm">
            <Checkbox
              id="test-mode-checkbox"
              checked={testMode}
              onCheckedChange={(v) => setTestMode(v === true)}
              disabled={applying}
            />
            테스트모드 (모든 가격 0)
          </label>

          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void buildPlan()}
              disabled={!owned || planning || applying}
            >
              {planning ? '미리보기 갱신 중…' : '미리보기 새로고침'}
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription className="whitespace-pre-wrap break-words">{error}</AlertDescription>
          </Alert>
        )}

        {plan && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium text-foreground">
              미리보기 — 생성 {plan.creates.length} · 갱신 {plan.updates.length} · 비활성{' '}
              {plan.deactivates.length} · 동일 {plan.skips.length}
            </div>
            <ScrollArea className="h-[280px] rounded-md border bg-muted/40">
              {/* whitespace-pre-wrap + break-all: 긴 metadata JSON(공백 없는 라인)이
                  팝업 밖으로 넘쳐 레이아웃이 깨지지 않도록 강제 줄바꿈한다. */}
              <pre className="whitespace-pre-wrap break-all p-3 text-xs">
                {[
                  ...plan.creates.map(
                    (c) =>
                      `[CREATE] ${c.payload.productId} (${c.payload.name}, ${c.payload.price} VX)`
                  ),
                  ...plan.updates.map(
                    (u) =>
                      `[UPDATE] ${u.payload.productId} (${u.payload.name}, ${u.payload.price} VX)\n` +
                      u.diffs
                        .map(
                          (d) =>
                            `   ${d.field}: ${JSON.stringify(d.old)} → ${JSON.stringify(d.new)}`
                        )
                        .join('\n')
                  ),
                  ...plan.deactivates.map(
                    (d) => `[DEACTIVATE] ${d.payload.productId} (${d.payload.name})`
                  ),
                  ...plan.skips.map((s) => `[SKIP] ${s}`),
                ].join('\n') || '(변경사항 없음)'}
              </pre>
            </ScrollArea>
          </div>
        )}

        {applyResult && (
          <Alert variant={applyResult.failed > 0 ? 'destructive' : 'default'}>
            <AlertTitle>완료</AlertTitle>
            <AlertDescription className="whitespace-pre-wrap break-words">
              생성 {applyResult.created} · 갱신 {applyResult.updated} · 비활성{' '}
              {applyResult.deactivated} · 실패 {applyResult.failed}
              {applyResult.failures.length > 0 && `\n${applyResult.failures.join('\n')}`}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} disabled={applying}>
            닫기
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            disabled={!owned || planning || applying || changeCount === 0}
          >
            {applying
              ? '적용 중…'
              : `적용 (생성 ${plan?.creates.length ?? 0} · 갱신 ${plan?.updates.length ?? 0} · 비활성 ${plan?.deactivates.length ?? 0})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
